<template>
  <div class="home-container">
    <div class="container">
      <h1 class="main-title">策略游戏合集</h1>
      <p class="subtitle">挑战你的智慧与策略思维</p>

      <div class="games-grid">
        <router-link
          to="/puzzle-game"
          class="game-card"
          @click="recordClick('puzzle')"
        >
          <div class="game-icon">🟦</div>
          <h2 class="game-title">移动格子</h2>
          <p class="game-description">
            5×5网格中移动彩色格子，让每种颜色连成一排。考验空间思维和策略规划能力。
          </p>
          <span class="game-difficulty difficulty-hard">困难</span>
        </router-link>

        <router-link
          to="/dots-boxes"
          class="game-card"
          @click="recordClick('dots')"
        >
          <div class="game-icon">⚫</div>
          <h2 class="game-title">点线成方</h2>
          <p class="game-description">
            6×6点阵中连接相邻点，围成方块得分。经典的双人对战策略游戏。
          </p>
          <span class="game-difficulty difficulty-medium">中等</span>
        </router-link>
      </div>

      <div class="stats">
        <div class="stat-item">
          <span class="stat-number">2</span>
          <span class="stat-label">游戏数量</span>
        </div>
        <div class="stat-item">
          <span class="stat-number">{{ totalPlays }}</span>
          <span class="stat-label">游戏次数</span>
        </div>
      </div>

      <div class="footer">
        <p>💡 选择一个游戏开始你的策略之旅</p>
        <p>🧠 锻炼逻辑思维，提升智力水平</p>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from "vue";

// 组件名称，用于keep-alive
defineOptions({
  name: 'HomeView'
});

// Reactive data
const totalPlays = ref(0);

// Lifecycle hooks
onMounted(() => {
  // 从localStorage获取游戏统计
  const puzzleClicks = parseInt(localStorage.getItem("puzzle_clicks") || "0");
  const dotsClicks = parseInt(localStorage.getItem("dots_clicks") || "0");
  totalPlays.value = puzzleClicks + dotsClicks;
});

// Methods
const recordClick = (gameName: string) => {
  let clicks = parseInt(localStorage.getItem(`${gameName}_clicks`) || "0");
  clicks++;
  localStorage.setItem(`${gameName}_clicks`, clicks.toString());
  totalPlays.value++;
};
</script>

<style lang="scss" scoped>
.home-container {
  min-height: 100vh;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.container {
  background: rgba(255, 255, 255, 0.95);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.1);
  backdrop-filter: blur(10px);
  max-width: 500px;
  width: 100%;
  text-align: center;
}

.main-title {
  font-size: clamp(28px, 8vw, 48px);
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 15px;
  background: linear-gradient(45deg, #667eea, #764ba2);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.subtitle {
  font-size: clamp(14px, 4vw, 18px);
  color: #7f8c8d;
  margin-bottom: 40px;
  line-height: 1.6;
}

.games-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 20px;
  margin-bottom: 30px;
}

.game-card {
  background: white;
  border-radius: 15px;
  padding: 25px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 2px solid transparent;
  text-decoration: none;
  color: inherit;
  display: block;

  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.15);
    border-color: #667eea;
  }

  &:active {
    transform: translateY(-2px);
  }

  &:nth-child(1) {
    animation-delay: 0.1s;
  }
  &:nth-child(2) {
    animation-delay: 0.2s;
  }
}

.game-icon {
  font-size: 48px;
  margin-bottom: 15px;
  display: block;
}

.game-title {
  font-size: clamp(18px, 5vw, 24px);
  font-weight: bold;
  color: #2c3e50;
  margin-bottom: 10px;
}

.game-description {
  font-size: clamp(12px, 3vw, 14px);
  color: #7f8c8d;
  line-height: 1.5;
  margin-bottom: 15px;
}

.game-difficulty {
  display: inline-block;
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: bold;
  text-transform: uppercase;

  &.difficulty-easy {
    background-color: #2ecc71;
    color: white;
  }

  &.difficulty-medium {
    background-color: #f39c12;
    color: white;
  }

  &.difficulty-hard {
    background-color: #e74c3c;
    color: white;
  }
}

.stats {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 15px;
  margin-top: 20px;
}

.stat-item {
  background: #f8f9fa;
  padding: 15px;
  border-radius: 10px;
  text-align: center;
}

.stat-number {
  font-size: clamp(20px, 6vw, 28px);
  font-weight: bold;
  color: #667eea;
  display: block;
}

.stat-label {
  font-size: clamp(10px, 2.5vw, 12px);
  color: #7f8c8d;
  text-transform: uppercase;
  letter-spacing: 1px;
}

.footer {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #ecf0f1;
  color: #95a5a6;
  font-size: clamp(12px, 3vw, 14px);
}

@media (max-width: 480px) {
  .container {
    padding: 25px;
    margin: 10px;
  }

  .games-grid {
    grid-template-columns: 1fr;
    gap: 15px;
  }

  .game-card {
    padding: 20px;
  }

  .stats {
    grid-template-columns: 1fr;
    gap: 10px;
  }
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}
</style>
